import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, SelectProps, Skeleton, notification } from "antd";
import { useSearchParams } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiChevronDown } from "@mdi/js";
import ListingSearchBar from "./components/ListingSearchBar";
import ListingFilterBar from "./components/ListingFilterBar";
import FiltersPanel from "./components/FiltersPanel";
import ResultsToolbar from "./components/ResultsToolbar";
import PromoTourCard from "./components/PromoTourCard";
import { brandLogoForCategory, brandTooltipText, useGdBrands } from "./brand-logos";
import SeriesApi from "../../../api-services/series-api.service";
import ListApi from "../../../api-services/list-api.service";
import LocationsApi from "../../../api-services/locations-api.service";
import TagsApi from "../../../api-services/tags-api.service";
import useBreakpoint from "../../../utils/useBreakpoint";
import {
  ListingFilterState,
  SortValue,
  activeFilterCount,
  parseListingParams,
  toQueryString,
  toSeriesSearchParams,
} from "../../../utils/listingSearchParams";
import { EX_END_FULL_LOCATION } from "../../../enums/departure_location.enum";
import type { Itinerary } from "../../../interfaces/itinerary.interface";
import type { Country } from "../../../interfaces/country.interface";
import type { Tag } from "../../../interfaces/tag.interface";
import { orderTagsForFilter } from "../../../utils/tagDisplay";
import type { SeriesSearchParams } from "../../../interfaces/search-params.interface";

const RESULTS_PER_PAGE = 12;

const formatRinggit = (currency: string | undefined, value: number | undefined): string | undefined => {
  if (value === undefined || !Number.isFinite(value)) return undefined;
  const isWhole = Number.isInteger(value);
  const formatted = value.toLocaleString("en-US", {
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: 2,
  });
  const prefix = currency === "MYR" ? "RM" : currency || "";
  return `${prefix} ${formatted}`.trim();
};

const itineraryUrl = (itinerary: Itinerary, type: string | null) => {
  const query = new URLSearchParams();
  if (type) query.set("type", type);
  if (itinerary.departureDate?.[0]) query.set("depart_date", itinerary.departureDate[0]);
  const queryString = query.toString();
  return `/web/itinerary/${itinerary.code}${queryString ? `?${queryString}` : ""}`;
};

export default function ListingPage() {
  const isMobile = useBreakpoint();
  const seriesApi = useMemo(() => new SeriesApi(), []);
  const listApi = useMemo(() => new ListApi(), []);
  const locationsApi = useMemo(() => new LocationsApi(), []);

  const [searchParams, setSearchParams] = useSearchParams();
  const queryString = searchParams.toString();
  const filters = useMemo(() => parseListingParams(searchParams), [queryString]);

  const [itineraries, setItineraries] = useState<Itinerary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [visibleCount, setVisibleCount] = useState(RESULTS_PER_PAGE);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [itineraryOptions, setItineraryOptions] = useState<{ value: string; label: string }[]>([]);
  const [cityOptions, setCityOptions] = useState<{ value: string; label: string }[]>([]);
  const [exEnds, setExEnds] = useState<SelectProps["options"]>([]);
  const [brands, setBrands] = useState<SelectProps["options"]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);

  const [filtersOpen, setFiltersOpen] = useState(false);

  const { brands: gdBrands } = useGdBrands();

  // b2c2b/api/v1/series (#4336) may not be deployed yet on the backend -
  // fall back to the legacy v1 endpoint on a 404 so the Listing page keeps
  // working (without the brand+cruise fix) until it ships, instead of
  // erroring out for every visitor.
  const useV1Fallback = useRef(false);
  const fetchListing = (params: SeriesSearchParams) => {
    if (useV1Fallback.current) return seriesApi.getItineraries(params);

    return seriesApi.getItinerariesV2(params).catch((error) => {
      if (error?.response?.status === 404) {
        useV1Fallback.current = true;
        return seriesApi.getItineraries(params);
      }
      throw error;
    });
  };

  useEffect(() => {
    let active = true;

    listApi.getExEndOptions().then((options) => active && setExEnds(options)).catch(() => {});
    listApi.getBrands().then((options) => active && setBrands(options)).catch(() => {});
    locationsApi.getCountries().then(({ locations }) => active && setCountries(locations as Country[])).catch(() => {});

    return () => {
      active = false;
    };
  }, [listApi, locationsApi]);

  useEffect(() => {
    let active = true;
    const activeType = filters.type === "cruise" ? "cruise" : "series";
    const fetchItineraries =
      filters.type === "cruise" ? listApi.getCruiseItineraries() : listApi.getItineraries();

    fetchItineraries
      .then((codes) => active && setItineraryOptions(codes.map(({ name }: any) => ({ value: name, label: name }))))
      .catch(() => {});

    // country_list carries the city names; cruise_country_list has none.
    if (filters.type === "cruise") {
      setCityOptions([]);
    } else {
      listApi.getCountryList()
        .then(({ cities }) => active && setCityOptions(cities.map(({ name }) => ({ value: name, label: name }))))
        .catch(() => {});
    }

    TagsApi.getTags(activeType).then((data) => active && setTags(data)).catch(() => {});

    return () => {
      active = false;
    };
  }, [listApi, filters.type]);

  useEffect(() => {
    setVisibleCount(RESULTS_PER_PAGE);

    let active = true;
    setLoading(true);
    setPage(1);

    fetchListing(toSeriesSearchParams(filters, 1))
      .then(({ itineraries: results, total: count }) => {
        if (!active) return;
        setItineraries(results || []);
        setTotal(count);
      })
      .catch(() => {
        if (!active) return;
        setItineraries([]);
        setTotal(0);
        notification.error({ message: "An error occured while loading itineraries." });
      })
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [queryString, seriesApi]);

  const showMore = () => {
    const next = visibleCount + RESULTS_PER_PAGE;

    if (next <= itineraries.length || itineraries.length >= total) {
      setVisibleCount(next);
      return;
    }

    const nextPage = page + 1;
    setLoadingMore(true);

    fetchListing(toSeriesSearchParams(filters, nextPage))
      .then(({ itineraries: more }) => {
        setItineraries((current) => [...current, ...(more || [])]);
        setPage(nextPage);
        setVisibleCount(next);
      })
      .catch(() => notification.error({ message: "An error occured while loading more itineraries." }))
      .finally(() => setLoadingMore(false));
  };

  const applyFilters = (next: ListingFilterState) => setSearchParams(toQueryString(next));

  const typeBrands = useMemo(() => {
    const activeType = filters.type === "cruise" ? "cruise" : "series";
    return ((brands as any[]) || []).find((group) => group.type === activeType)?.options ?? [];
  }, [brands, filters.type]);

  const tagNamesFor = (itinerary: Itinerary): string[] =>
    orderTagsForFilter(itinerary.tags || [], filters.tags).map((tag) => tag.name);

  const hasMore = visibleCount < total;

  return (
    <div id="listing-page-v2">
      <div className="v2-listing__header">
        <div className="v2-listing__header-inner">
          <h1 className="v2-listing__title">{filters.type === "cruise" ? "Cruise" : "Tour"}</h1>
          <p className="v2-listing__subtitle">
            {filters.type === "cruise"
              ? "Find your favourite cruise and set sail on your next adventure."
              : "Find your favourite tour and start your next adventure."}
          </p>
          <ListingSearchBar
            isMobile={isMobile}
            filters={filters}
            itineraryOptions={itineraryOptions}
            cityOptions={cityOptions}
            exEnds={exEnds}
            brands={typeBrands}
            countries={countries}
            onSearch={applyFilters}
          />
          <ListingFilterBar
            isMobile={isMobile}
            filters={filters}
            countries={countries}
            tags={tags}
            filterCount={activeFilterCount(filters)}
            onOpenFilters={() => setFiltersOpen(true)}
            onChange={applyFilters}
          />
        </div>
      </div>

      <div className="v2-listing__body">
        <ResultsToolbar
          total={total}
          sort={filters.sort}
          onSortChange={(sort: SortValue) => applyFilters({ ...filters, sort })}
          showSort={!isMobile}
        />

        {loading ? (
          <div className="v2-listing__grid">
            {Array.from({ length: 8 }, (_, index) => (
              <div className={`v2-offer-card v2-offer-card--${isMobile ? "stacked" : "grid"} v2-offer-card--loading`} key={index}>
                <div className="v2-offer-card__media">
                  <Skeleton active />
                </div>
                <div className="v2-offer-card__body">
                  <Skeleton active paragraph={{ rows: 3 }} title={{ width: "80%" }} />
                </div>
              </div>
            ))}
          </div>
        ) : itineraries.length === 0 ? (
          <div className="v2-listing__empty">
            <p>No holidays match your search.</p>
            <p>Try widening your dates, brands or destination.</p>
          </div>
        ) : (
          <>
            <div className="v2-listing__grid">
              {itineraries.slice(0, visibleCount).map((itinerary, index) => {
                const departureDates = itinerary.departureDate || [];
                const guaranteed = departureDates.some((date) =>
                  itinerary.guranteedDepartureDates?.includes(date)
                );
                const exEnd = itinerary.departureLocations?.[0];
                const brandDescription = brandTooltipText(gdBrands, itinerary.category);
                const [ribbon, ...otherTags] = tagNamesFor(itinerary);

                return (
                  <PromoTourCard
                    key={`${itinerary.id}-${index}`}
                    variant={isMobile ? "stacked" : "grid"}
                    href={itineraryUrl(itinerary, filters.type)}
                    image={itinerary.images?.[0]}
                    brandLogo={brandLogoForCategory(itinerary.category)}
                    brandDescription={brandDescription}
                    destination={itinerary.country}
                    departFrom={exEnd ? EX_END_FULL_LOCATION[exEnd] ?? exEnd : undefined}
                    title={itinerary.caption}
                    guaranteed={guaranteed}
                    price={formatRinggit(itinerary.priceCurrency, itinerary.priceValue)}
                    ribbon={ribbon}
                    tags={otherTags}
                    originalPrice={formatRinggit(itinerary.priceCurrency, itinerary.normalPriceValue)}
                    save={itinerary.saveAmount ? formatRinggit(itinerary.priceCurrency, itinerary.saveAmount) : undefined}
                  />
                );
              })}
            </div>

            {hasMore && (
              <div className="v2-listing__more">
                <Button type="primary" loading={loadingMore} onClick={showMore}>
                  Show More <Icon path={mdiChevronDown} size="18px" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <FiltersPanel
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        isMobile={isMobile}
        filters={filters}
        tags={tags}
        brands={typeBrands}
        onApply={applyFilters}
      />
    </div>
  );
}
